import type { ReactElement } from "react";

import type { QueryObserverResult } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import type { ITopCompanyItem } from "app/pages/sales-intelligence/modules/top-companies/types/TopCompany";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import homePageTrackingService from "app/pages/sales-intelligence/services/tracking/home-page/trackingService";
import {
    useIsContactBeingRevealed,
    useIsContactNotRevealed,
} from "app/pages/sales-intelligence/sub-modules/common/hooks/useContactRevealStatus";
import ContactDetailsList from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactDetails/ContactDetailsList";
import ContactDetailsOverlayStateWithTermsGuard from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactDetailsOverlayStateWithTermsGuard/ContactDetailsOverlayStateWithTermsGuard";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import {
    selectContactsCost,
    selectRemainingQuota,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import { selectIsContactsReadonly } from "app/pages/sales-intelligence/sub-modules/contacts-tab/store/selectors";

import withDisabledByAccountOverlay from "../hoc/withDisabledByAccountOverlay";
import withDisabledByCountryOverlay from "../hoc/withDisabledByCountryOverlay";
import useTopCompanyContactReveal from "../hooks/useTopCompanyContactReveal";

import { StyledContactDetailsContainer, StyledTopCompaniesContactDetailsOverlay } from "./styles";

const ContactDetailsOverlay = withDisabledByCountryOverlay(
    withDisabledByAccountOverlay(ContactDetailsOverlayStateWithTermsGuard),
);

type Props = {
    isDisabledTooltip: boolean;
    isDisabledContact: boolean;
    selectedContact: ContactSearchEntry;
    onContactUs: VoidFunction;
    onRefetchList: () => Promise<QueryObserverResult<readonly ITopCompanyItem[], unknown>>;
};

const ContactDetailsContainer = ({
    isDisabledTooltip,
    isDisabledContact,
    onRefetchList,
    selectedContact,
    onContactUs,
}: Props): ReactElement => {
    const isContactsAvailabilityReadonly = useSelector(selectIsContactsReadonly);
    const remainingRevealQuota = useSelector(selectRemainingQuota);
    const contactsCost = useSelector(selectContactsCost);
    const isContactIdNotRevealed = useIsContactNotRevealed(selectedContact.id);
    const isContactBeingRevealed = useIsContactBeingRevealed(selectedContact.id);

    const { isLoading: isRevealLoading, reveal } = useTopCompanyContactReveal(selectedContact);

    const isLoading = isRevealLoading || isContactBeingRevealed;

    const { isRevealed, hasValidEmails, hasDirectPhoneNumbers, hasMobilePhoneNumbers } = selectedContact;
    const isDataCreditsEnabled = salesSettingsHelper.isDataCreditsEnabled;

    const hasDetailsToReveal = hasValidEmails || hasDirectPhoneNumbers || hasMobilePhoneNumbers;

    const remainingRevealItems = Math.floor(remainingRevealQuota / contactsCost);
    const isNotEnoughCredits = remainingRevealItems <= 0;

    const handleContactReveal = (): void => {
        if (isNotEnoughCredits) {
            return;
        }

        trackingService.trackRevealContactClick();

        reveal();
    };

    const handleTrackReveal = () => {
        homePageTrackingService.trackRevealContact();
    };

    const contactData = isDisabledContact
        ? { ...selectedContact, isRevealed: false, privateData: undefined }
        : selectedContact;

    return (
        <StyledContactDetailsContainer>
            <ContactDetailsList rowData={contactData} />

            <ContactDetailsOverlay
                buttonType="flat"
                isLoading={isLoading}
                isDisabledTooltip={isDisabledTooltip}
                isRevealed={isRevealed}
                isDataCreditsEnabled={isDataCreditsEnabled}
                hasDetailsToReveal={hasDetailsToReveal}
                isContactsAvailabilityReadonly={isContactsAvailabilityReadonly}
                remainingRevealQuota={remainingRevealQuota}
                contactsCost={contactsCost}
                isContactIdNotRevealed={isContactIdNotRevealed}
                actionsWrapperComponent={StyledTopCompaniesContactDetailsOverlay}
                onReveal={handleContactReveal}
                onContactUs={onContactUs}
                onApprove={onRefetchList}
                onClickBeforeApprove={handleTrackReveal}
            />
        </StyledContactDetailsContainer>
    );
};

export default ContactDetailsContainer;
